import {
  Autosuggest,
  Box,
  Button,
  ColumnLayout,
  DatePicker,
  ExpandableSection,
  Input,
  Modal,
  Select,
  SelectProps,
  SpaceBetween,
  Textarea,
} from '@cloudscape-design/components';
import { useMutation } from '@tanstack/react-query';
import { PlaceListCreate, PlaceList } from '../common/types';
import { createList } from '../common/api/lists';
import { useState } from 'react';
import {
  Container,
  Header,
  AppLayout,
  ContentLayout,
  Grid,
  Toggle,
  Form,
  Alert,
  FormField,
  StatusIndicator,
} from '@cloudscape-design/components';
import { ErrorMessage } from './ErrorMessage';

export type CreateListModalProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  onComplete?: (placeList: PlaceList) => void;
};

export function CreateListModal(props: CreateListModalProps) {
  const { open, setOpen, onComplete } = props;

  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');

  const isInvalid = !name.trim();

  const close = () => {
    setOpen(false);
    setName('');
    setDescription('');
  };

  const create = useMutation({
    mutationFn: createList,
    onSuccess: (data) => {
      if (data && onComplete) {
        onComplete(data);
      }
      create.reset();
      close();
    },
  });

  const handleCreate = () => {
    if (create.isPending) {
      return;
    }
    if (isInvalid) {
      return;
    }

    const data: PlaceListCreate = {
      name: name.trim(),
      description: description.trim() ?? undefined,
    };
    create.mutate(data);
  };

  return (
    <Modal
      size="medium"
      onDismiss={close}
      visible={open}
      closeAriaLabel="Close"
      header="Create a new list"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close}>
              Cancel
            </Button>
            <Button
              loading={create.isPending}
              variant="primary"
              onClick={handleCreate}
              disabled={isInvalid}
            >
              Create
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <FormField
          description="The name of the list to create."
          constraintText="Must be unique."
          label="List Name"
        >
          <Input
            autoFocus
            onChange={({ detail }) => setName(detail.value)}
            value={name}
            placeholder="The name of the list..."
          />
        </FormField>
        <FormField description="A description of the list." label="Description">
          <Input
            onChange={({ detail }) => setDescription(detail.value)}
            value={description}
            placeholder="The description of the list..."
          />
        </FormField>
        {create.error && <ErrorMessage loading={create.isPending} error={create.error} />}
      </SpaceBetween>
    </Modal>
  );
}
